import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { Calendar, Package, X } from "lucide-react";
import PedidoItems from "./Items";

const c = {
  greenMd: "#2D6B45",
  gold: "#E8A020",
  ink: "#0F1F17",
  muted: "#6B7D74",
  surface: "#F4F6F5",
  border: "rgba(28,74,46,0.1)",
  white: "#FFFFFF",
  surface2: "#E8EDEA",
};

interface Pedido {
  pedido_id: number;
  estado: string;
  fecha_creacion: string;
}

interface Props {
  pedidoView: boolean;
  setPedidoView: React.Dispatch<React.SetStateAction<boolean>>;
  pedidoViewID: number;
}

function formatDateLabel(dateString: string | undefined) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
}

function estadoPillLabel(estado: string | undefined) {
  if (!estado) return "—";
  const e = estado.toLowerCase();
  if (e.includes("pend")) return "Pendiente";
  if (e.includes("camino") || e.includes("enví")) return "En envío";
  return estado;
}

const PedidoView = ({
  pedidoView,
  setPedidoView,
  pedidoViewID,
}: Props) => {
  const [isClosing, setIsClosing] = useState(false);
  const [pedido, setPedido] = useState<Pedido | null>(null);

  const getPedido = useCallback(async (id: number) => {
    if (!id) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/pedidos/${id}`,
        {
          headers: {
            "auth-token": `${sessionStorage.getItem("token")}`,
          },
        }
      );
      setPedido(response.data as Pedido);
    } catch {
      setPedido(null);
    }
  }, []);

  useEffect(() => {
    if (pedidoView === false) {
      const t = setTimeout(() => {
        setPedido(null);
      }, 320);
      return () => clearTimeout(t);
    }
    if (pedidoViewID) {
      getPedido(pedidoViewID);
    }
  }, [pedidoView, pedidoViewID, getPedido]);

  const close = () => {
    if (pedidoView) {
      setIsClosing(true);
      setTimeout(() => {
        setPedidoView(false);
        setIsClosing(false);
      }, 280);
    }
  };

  if (!pedidoView && !isClosing) {
    return null;
  }

  return (
    <>
      <div
        role="presentation"
        className={`fixed inset-0 z-[940] bg-black/50 transition-opacity duration-200 ${
          isClosing ? "opacity-0" : "opacity-100"
        }`}
        onClick={close}
        onKeyDown={(e) => e.key === "Escape" && close()}
        style={{ maxWidth: "100vw" }}
      />
      <div
        className="fixed bottom-0 left-1/2 z-[950] flex max-h-[min(90vh,100%)] w-full max-w-[390px] flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl"
        style={{
          boxShadow: "0 -8px 40px rgba(0,0,0,0.2)",
          transform: isClosing
            ? "translate(-50%, 100%)"
            : "translate(-50%, 0)",
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          className="mx-auto mt-3 h-1 w-9 shrink-0 rounded-sm"
          style={{ background: "rgba(0,0,0,0.12)" }}
        />
        <div
          className="flex h-[180px] shrink-0 items-center justify-center"
          style={{ background: c.surface }}
        >
          <span className="text-7xl leading-none" aria-hidden>
            📦
          </span>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-6 pt-2">
          <p
            className="mb-1 text-[0.7rem] font-semibold uppercase tracking-wider"
            style={{ color: c.greenMd }}
          >
            Canje
          </p>
          <h2
            className="mb-2 text-xl font-extrabold leading-tight"
            style={{ color: c.ink }}
          >
            {pedido ? `Pedido #${pedido.pedido_id}` : "Cargando…"}
          </h2>
          <p
            className="mb-4 text-sm leading-relaxed"
            style={{ color: c.muted }}
          >
            {pedido
              ? `Solicitado el ${formatDateLabel(pedido.fecha_creacion)}. Los artículos listados abajo forman parte de tu canje; el envío y seguimiento dependen del estado indicado.`
              : "Cargando detalle del pedido…"}
          </p>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div
              className="flex min-w-0 items-center gap-1.5 text-2xl font-extrabold"
              style={{ color: c.gold }}
            >
              <span
                className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-white"
                style={{ background: c.gold }}
                aria-hidden
              >
                <Calendar className="h-3.5 w-3.5" strokeWidth={2.5} />
              </span>
              {pedido
                ? formatDateLabel(pedido.fecha_creacion)
                : "—"}
            </div>
            <span
              className="ml-auto inline-flex max-w-[55%] items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
              style={{ background: c.surface2, color: c.muted }}
            >
              <Package className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate" title={pedido?.estado ?? ""}>
                {pedido
                  ? estadoPillLabel(pedido.estado)
                  : "—"}
              </span>
            </span>
          </div>
          <p
            className="mb-1.5 text-[0.65rem] font-bold uppercase tracking-wider"
            style={{ color: c.muted }}
          >
            Artículos del pedido
          </p>
          <div
            className="mb-4 overflow-hidden rounded-2xl border border-[1.5px] p-2.5"
            style={{ borderColor: c.border, background: c.white }}
          >
            <PedidoItems pedidoViewID={pedidoViewID} />
          </div>
          <button
            type="button"
            onClick={close}
            className="flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-base font-bold text-white transition hover:opacity-95"
            style={{ background: c.greenMd }}
          >
            Cerrar
          </button>
          <button
            type="button"
            onClick={close}
            className="mt-2 w-full rounded-xl py-2.5 text-sm font-semibold"
            style={{ color: c.muted }}
          >
            Volver al listado
          </button>
        </div>
        <button
          type="button"
          onClick={close}
          className="absolute right-3 top-3 rounded-lg p-1.5"
          style={{ color: c.muted }}
          aria-label="Cerrar"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </>
  );
};

export default PedidoView;
